/**
 * Contenedor de inyección de dependencias simple
 * Permite registrar y resolver dependencias
 */

import {FormRepository} from 'domain/ports';
import {GuideRepository} from 'domain/ports';
import {AnswerRepository} from 'domain/ports';
import {ScoreRepository} from 'domain/ports';
import {ScoreCalculator} from 'domain/ports';
import {ApiFormRepository} from 'infrastructure/repositories';
import {DefaultEvaluationService, DefaultScoreCalculator} from 'infrastructure/services';
import {FormService} from "application/services/FormService";
import {ResultsService} from "application/services/ResultsService";

// Tipo para el registro de dependencias
type DependencyRegistry = {
  [key: string]: any;
};

// Singleton para el contenedor de dependencias
class DependencyContainer {
  private static instance: DependencyContainer;
  private registry: DependencyRegistry = {};

  private constructor() {
    // Inicializar con las implementaciones por defecto
    this.registerDefaults();
  }

  public static getInstance(): DependencyContainer {
    if (!DependencyContainer.instance) {
      DependencyContainer.instance = new DependencyContainer();
    }
    return DependencyContainer.instance;
  }

  private registerDefaults(): void {
    // Registrar implementaciones de repositorios
    // Usar ApiFormRepository por defecto en lugar de JsonFormRepository
    const formRepository = new ApiFormRepository();
    this.register('FormRepository', formRepository);
    this.register('GuideRepository', formRepository);
    this.register('AnswerRepository', formRepository);
    this.register('ScoreRepository', formRepository);

    //aqui modifique
    const evaluationService = new DefaultEvaluationService();
    this.register('DefaultEvaluationService', evaluationService);

    // Registrar implementación del calculador de puntuaciones
    const scoreCalculator = new DefaultScoreCalculator();
    this.register('ScoreCalculator', scoreCalculator);

    // Registrar servicios de aplicación
    const formService = new FormService(formRepository, scoreCalculator);
    this.register('FormService', formService);

    const resultsService = new ResultsService(formRepository, scoreCalculator);
    this.register('ResultsService', resultsService);
  }

  /**
   * Configura el contenedor para usar ApiFormRepository
   * @param apiBaseUrl URL base opcional para la API
   */
  public useApiRepository(apiBaseUrl?: string): void {
    const formRepository = new ApiFormRepository(apiBaseUrl);
    this.register('FormRepository', formRepository);
    this.register('GuideRepository', formRepository);
    this.register('AnswerRepository', formRepository);
    this.register('ScoreRepository', formRepository);
    
    // Actualizar servicios
    const scoreCalculator = this.resolve<ScoreCalculator>('ScoreCalculator');
    const formService = new FormService(formRepository, scoreCalculator);
    this.register('FormService', formService);
    
    const resultsService = new ResultsService(formRepository, scoreCalculator);
    this.register('ResultsService', resultsService);
  }

  public register<T>(key: string, implementation: T): void {
    this.registry[key] = implementation;
  }

  public resolve<T>(key: string): T {
    const implementation = this.registry[key];
    if (!implementation) {
      throw new Error(`No implementation registered for ${key}`);
    }
    return implementation as T;
  }
}

// Exportar una instancia del contenedor
export const container = DependencyContainer.getInstance();

// Funciones helper para obtener implementaciones específicas
export const getFormRepository = (): FormRepository => container.resolve<FormRepository>('FormRepository');
export const getGuideRepository = (): GuideRepository => container.resolve<GuideRepository>('GuideRepository');
export const getAnswerRepository = (): AnswerRepository => container.resolve<AnswerRepository>('AnswerRepository');
export const getScoreRepository = (): ScoreRepository => container.resolve<ScoreRepository>('ScoreRepository');
export const getScoreCalculator = (): ScoreCalculator => container.resolve<ScoreCalculator>('ScoreCalculator');
export const getFormService = (): FormService => container.resolve<FormService>('FormService');
export const getResultsService = (): ResultsService => container.resolve<ResultsService>('ResultsService');

//aqui modifique
export const getEvaluationService = (): DefaultEvaluationService =>
    container.resolve<DefaultEvaluationService>('DefaultEvaluationService');

// Funciones para cambiar la implementación del repositorio
export const useApiRepository = (apiBaseUrl?: string): void => (container as DependencyContainer).useApiRepository(apiBaseUrl);
